import React from 'react';
import { useParams } from 'next/navigation';
import MainSearch from '@/components/search/MainSearch';
import CitySearchOptions from '@/components/form/inputs/CitySearchOptions';
import MobileCityPicker from '@/components/job-title/city-picker/MobileCityPicker';
import { CascadeOption } from '@/components/form/inputs/CascadeSelect';
import { Option } from '@/context/job-builder';
import { JobTitleContext } from '@/context/create-context';
import { NATION_WIDE } from '@/constants/city';
import { JOB_TITLE_PAGE_SEARCH_INPUT_ID } from '@/constants/component-id';
import { getJobTitleCity, getJobCompanyCity } from '@/api/tanzhi';

export type JobOptionType = {
  id: string;
  name: string;
  nameType?: string;
  aliasId?: string;
  city?: { name: string; pinyin: string };
  search_type?: 'company' | 'job_title' | 'job_title_city';
};

interface CityOption extends Option {
  pinyin?: string;
}

type Props = {
  jobValue: JobOptionType;
  cityValue: CityOption | undefined;
  handleChange: (val: JobOptionType) => void;
  handleCityChange: (val: CityOption | undefined) => void;
  pageUniqueId: string;
  jnClassName?: string;
  searchError?: string;
  preferenceValue?: JobOptionType | null;
  location?: CityOption | null;
  multiSearch?: boolean;
  searchType?: string;
};

const JobTitleSearch = (props: Props) => {
  const {
    jobValue,
    cityValue,
    handleChange,
    handleCityChange,
    pageUniqueId,
    jnClassName = 'flex-1',
    searchError = '',
    preferenceValue,
    location,
    multiSearch,
    searchType,
  } = props;
  const { jobTitle, displayName } = React.useContext(JobTitleContext);
  const [cityPickerOpen, setCityPickerOpen] = React.useState(false);
  const [cityOptions, setCityOptions] = React.useState<CascadeOption[]>([
    NATION_WIDE,
    ...jobTitle.province_city_data,
  ]);
  const searchParams = useParams();
  const city = searchParams?.city as string;

  const handleMobileSearch = () => setCityPickerOpen(true);

  const fetchCityData = (
    fetchFunction: (id: string) => Promise<any>,
    id: string,
  ) => {
    return fetchFunction(id);
  };

  const handleSearch = (value: JobOptionType, location?: CityOption | null) => {
    handleChange(value);
    if (value.id !== jobValue?.id) {
      const searchType = value.search_type;
      fetchCityData(
        searchType === 'company' ? getJobCompanyCity : getJobTitleCity,
        value.id,
      )
        .then((res) => {
          setCityOptions([NATION_WIDE, ...res.data]);
        })
        .finally(() => {
          handleCityChange(location || NATION_WIDE);
        });
    }
  };

  React.useEffect(() => {
    const findValue = city
      ? cityOptions
          ?.map((item) => {
            if ('children' in item) {
              const childValue = item['children']?.find((cItem) => {
                const { name, pinyin } = cItem;
                return (
                  name === city || name.slice(0, -1) === city || pinyin == city
                );
              });
              if (childValue) {
                return childValue;
              }
            }
          })
          .filter(Boolean)
      : undefined;
    handleCityChange(
      findValue && findValue?.length > 0 ? findValue[0] : NATION_WIDE,
    );
  }, [city]);

  React.useEffect(() => {
    if (jobTitle && displayName) {
      const { id, name, aliases } = jobTitle;
      const filteredAlias = aliases
        .filter((alias) => alias.name === displayName)
        .pop();
      id &&
        handleChange({
          id,
          name: displayName,
          nameType: displayName === name ? 'primary' : 'alias',
          aliasId: filteredAlias ? filteredAlias.id : '',
        });
    }
  }, [displayName]);

  React.useEffect(() => {
    preferenceValue && handleSearch(preferenceValue, location);
  }, [preferenceValue]);

  return (
    <>
      <div className={jnClassName}>
        <MainSearch
          id={JOB_TITLE_PAGE_SEARCH_INPUT_ID}
          value={jobValue}
          onChange={handleSearch}
          showSearchIcon={false}
          autoClassName="!w-full !overflow-hidden !rounded-sm"
          paperClassName="!bg-slate-50 !h-14 !px-4 !py-3.5"
          inputClassName="!text-sm data-[error=true]:!text-red-700"
          isNeedSearch={false}
          searchError={!!searchError}
          dataEleId="job-title"
          dataEleGroup="search_job-title"
          dataObserverId="search_job-title"
          multiSearch={multiSearch}
          searchType={searchType}
        >
          {searchError && (
            <p className="text-red-700 text-xs mt-2">{searchError}</p>
          )}
        </MainSearch>
      </div>
      <div className="flex-1">
        <CitySearchOptions
          options={cityOptions}
          value={cityValue}
          onChange={(selectValue) => {
            handleCityChange(selectValue);
          }}
          isHalfWidth={true}
          optionsClassName="!bg-white"
          handleMobileSearch={handleMobileSearch}
          pageUniqueId={pageUniqueId}
          dataEleId="city"
          dataEleGroup="search_city"
          dataObserverId="search_city"
        />
      </div>
      <MobileCityPicker
        value={cityValue}
        options={cityOptions}
        cityPickerOpen={cityPickerOpen}
        onCitySelect={(selectValue) => {
          handleCityChange(selectValue);
        }}
        onClose={() => setCityPickerOpen(false)}
      />
    </>
  );
};

export default JobTitleSearch;
